import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Users, Trophy, Zap, CheckCircle, XCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API_WS = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');
const API = process.env.REACT_APP_BACKEND_URL;

function TimerBar({ timer, timeLeft }) {
  if (!timer || timer === 0) return null;
  const pct = (timeLeft / timer) * 100;
  const color = pct > 50 ? '#06d6a0' : pct > 25 ? '#ffd93d' : '#ff6b6b';
  return (
    <div className="w-full h-3 bg-white/10 rounded-full overflow-hidden">
      <div className="h-full rounded-full transition-all duration-1000 ease-linear" style={{ width: `${pct}%`, backgroundColor: color }} />
    </div>
  );
}

export default function QuizLive() {
  const { pin } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [nickname, setNickname] = useState('');
  const [nicknameInput, setNicknameInput] = useState('');
  const [phase, setPhase] = useState('join'); // join, waiting, question, result, leaderboard, ended
  const [participants, setParticipants] = useState([]);
  const [currentQ, setCurrentQ] = useState(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [timer, setTimer] = useState(20);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [answerResult, setAnswerResult] = useState(null);
  const [score, setScore] = useState(0);
  const [rank, setRank] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [finalRankings, setFinalRankings] = useState([]);
  const [quizTitle, setQuizTitle] = useState('');
  const [textAnswer, setTextAnswer] = useState('');
  const ws = useRef(null);
  const timerRef = useRef(null);
  const participantId = useRef(null);
  // Refs pour éviter les stale closures dans les handlers WebSocket
  const phaseRef = useRef(phase);
  const handleMessageRef = useRef(null);

  useEffect(() => { phaseRef.current = phase; }, [phase]);

  const handleMessage = useCallback((msg) => {
    switch (msg.type) {
      case 'welcome':
        participantId.current = msg.participant_id;
        setNickname(msg.nickname);
        setPhase('waiting');
        setScore(msg.score || 0);
        break;
      case 'waiting_room':
        setParticipants(msg.participants || []);
        break;
      case 'session_started':
        setTotalQuestions(msg.total_questions || 0);
        toast.success('La session commence !');
        break;
      case 'question':
        clearInterval(timerRef.current);
        setCurrentQ(msg.question);
        setQuestionIndex(msg.index || 0);
        setTotalQuestions(msg.total || totalQuestions);
        setSelectedAnswer(null);
        setAnswerResult(null);
        setTextAnswer('');
        setTimer(msg.timer || 20);
        setTimeLeft(msg.timer || 20);
        setRank(msg.rank || null);
        if (msg.score !== undefined) setScore(msg.score);
        setPhase('question');
        if (msg.timer > 0) {
          timerRef.current = setInterval(() => {
            setTimeLeft(prev => {
              if (prev <= 1) { clearInterval(timerRef.current); return 0; }
              return prev - 1;
            });
          }, 1000);
        }
        break;
      case 'answer_result':
        clearInterval(timerRef.current);
        setAnswerResult(msg);
        setScore(msg.score || 0);
        setPhase('result');
        break;
      case 'skipped':
        setPhase('result');
        break;
      case 'leaderboard':
        setLeaderboard(msg.rankings || []);
        setPhase('leaderboard');
        break;
      case 'reveal':
        setAnswerResult(prev => prev ? { ...prev, correct_answers: msg.correct_answers, explanation: msg.explanation } : {
          question_id: msg.question_id,
          is_correct: false,
          correct_answers: msg.correct_answers,
          explanation: msg.explanation,
          points: 0,
        });
        break;
      case 'session_ended':
        clearInterval(timerRef.current);
        setFinalRankings(msg.final_rankings || []);
        setQuizTitle(msg.quiz_title || '');
        setPhase('ended');
        break;
      case 'error':
        toast.error(msg.message || 'Erreur');
        break;
      default:
        break;
    }
  }, [totalQuestions]);

  // Maintient la ref à jour — les handlers WS utilisent toujours la version fraîche
  useEffect(() => { handleMessageRef.current = handleMessage; }, [handleMessage]);

  const connect = useCallback((nick) => {
    ws.current = new WebSocket(`${API_WS}/api/quiz/ws/${pin}`);
    ws.current.onopen = () => {
      ws.current.send(JSON.stringify({
        action: 'join',
        nickname: nick,
        user_id: user?.id || null,
      }));
    };
    ws.current.onmessage = (e) => {
      // Utilise la ref pour toujours appeler la dernière version de handleMessage
      handleMessageRef.current(JSON.parse(e.data));
    };
    ws.current.onerror = () => toast.error('Connexion perdue');
    ws.current.onclose = () => {
      // Utilise phaseRef pour éviter la stale closure sur phase
      if (phaseRef.current !== 'ended') {
        toast.error('Déconnecté — tentative de reconnexion...');
        setTimeout(() => connect(nick), 3000);
      }
    };
  }, [pin, user]); // phase retiré des deps — géré via phaseRef

  useEffect(() => {
    return () => {
      clearInterval(timerRef.current);
      if (ws.current) ws.current.close();
    };
  }, []);

  const sendAnswer = (answer) => {
    if (!ws.current || ws.current.readyState !== 1 || selectedAnswer !== null) return;
    setSelectedAnswer(answer);
    clearInterval(timerRef.current);
    ws.current.send(JSON.stringify({
      action: 'answer',
      question_id: currentQ?.question_id,
      answer,
      time_remaining: timeLeft,
    }));
  };

  const sendSkip = () => {
    if (!ws.current || ws.current.readyState !== 1) return;
    setSelectedAnswer('__skip__');
    ws.current.send(JSON.stringify({ action: 'skip', question_id: currentQ?.question_id }));
  };

  // JOIN SCREEN
  if (phase === 'join') {
    return (
      <div className="min-h-screen bg-[#0f0e17] flex items-center justify-center px-4">
        <div className="max-w-sm w-full">
          <div className="text-center mb-6">
            <div className="text-5xl mb-3">🎮</div>
            <h1 className="text-xl font-heading font-bold text-white">Session #{pin}</h1>
            <p className="text-[#a0a0ab] text-sm mt-1">Entrez votre pseudo pour rejoindre</p>
          </div>
          <form onSubmit={(e) => { e.preventDefault(); if (nicknameInput.trim().length >= 2) connect(nicknameInput.trim()); }}>
            <input value={nicknameInput} onChange={e => setNicknameInput(e.target.value)}
              placeholder="Votre pseudo..." maxLength={20} autoFocus
              className="w-full bg-white/5 border border-white/10 text-white placeholder-[#a0a0ab] rounded-xl px-4 py-3 text-sm mb-3 focus:outline-none focus:border-[#ff6b6b]/50"
              data-testid="nickname-input" />
            <button type="submit" disabled={nicknameInput.trim().length < 2}
              className="w-full btn-primary py-3 text-sm disabled:opacity-50" data-testid="join-session-btn">
              Rejoindre la session
            </button>
          </form>
          <button onClick={() => navigate('/quiz')} className="w-full text-center text-[#a0a0ab] text-sm mt-3 hover:text-white">Retour</button>
        </div>
      </div>
    );
  }

  // WAITING ROOM
  if (phase === 'waiting') {
    return (
      <div className="min-h-screen bg-[#0f0e17] flex flex-col items-center justify-center px-4">
        <div className="text-center mb-6">
          <div className="text-4xl mb-2">⏳</div>
          <h1 className="text-xl font-bold text-white">Salle d'attente</h1>
          <p className="text-[#a0a0ab] text-sm mt-1">Le quiz commence quand l'hôte est prêt</p>
          <div className="bg-white/5 border border-white/10 rounded-xl px-6 py-3 mt-3 inline-block">
            <p className="text-xs text-[#a0a0ab]">Votre pseudo</p>
            <p className="text-white font-bold">{nickname}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-[#a0a0ab] text-sm mb-4">
          <Users size={14} /> {participants.length} joueur{participants.length > 1 ? 's' : ''} connecté{participants.length > 1 ? 's' : ''}
        </div>
        <div className="flex flex-wrap gap-2 max-w-xs justify-center">
          {participants.map(p => (
            <span key={p.participant_id} className={`text-xs px-2 py-1 rounded-full ${p.nickname === nickname ? 'bg-[#ff6b6b]/20 text-[#ff6b6b] border border-[#ff6b6b]/30' : 'bg-white/5 text-[#e8e8e8]'}`}>
              {p.nickname}
            </span>
          ))}
        </div>
        <div className="mt-6 flex gap-1">
          {[0, 1, 2].map(i => <div key={i} className="w-2 h-2 bg-[#ff6b6b] rounded-full animate-bounce" style={{ animationDelay: `${i * 0.2}s` }} />)}
        </div>
      </div>
    );
  }

  // QUESTION SCREEN (participant)
  if (phase === 'question' && currentQ) {
    const isAnswered = selectedAnswer !== null;
    return (
      <div className="min-h-screen bg-[#0f0e17] flex flex-col px-4 py-6 max-w-md mx-auto">
        <div className="flex items-center justify-between mb-3 text-sm text-[#a0a0ab]">
          <span>{questionIndex + 1} / {totalQuestions}</span>
          <span className="text-white font-bold">{score.toLocaleString()} pts</span>
          {rank && <span className="text-[#ffd93d] text-xs">#{rank}</span>}
        </div>
        <TimerBar timer={timer} timeLeft={timeLeft} />
        <div className="flex items-center justify-center h-8 mt-2">
          <span className="text-[#ffd93d] font-mono text-2xl font-bold">{timeLeft > 0 ? timeLeft : '-'}</span>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-2xl p-4 mt-2 mb-4 text-center">
          {currentQ.emoji && <div className="text-3xl mb-2">{currentQ.emoji}</div>}
          <p className="text-white text-sm font-semibold">{currentQ.text}</p>
          {isAnswered && <p className="text-[#a0a0ab] text-xs mt-2">En attente des autres joueurs...</p>}
        </div>

        {!isAnswered ? (
          <div className="grid grid-cols-2 gap-2 flex-1">
            {(currentQ.options || []).map((opt, i) => {
              const COLORS = ['bg-[#ff6b6b]/80', 'bg-[#06d6a0]/80', 'bg-[#ffd93d]/80 text-black', 'bg-purple-500/80'];
              return (
                <button key={i} onClick={() => sendAnswer(i)}
                  className={`${COLORS[i % COLORS.length]} text-white rounded-xl p-3 text-sm font-medium min-h-[60px] flex items-center justify-center text-center hover:opacity-90 active:scale-95 transition-all`}
                  data-testid={`live-answer-${i}`}>
                  {opt}
                </button>
              );
            })}
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="w-12 h-12 border-2 border-[#ff6b6b] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              <p className="text-[#a0a0ab] text-sm">Réponse envoyée</p>
            </div>
          </div>
        )}

        {!isAnswered && (
          <button onClick={sendSkip} className="mt-3 text-center text-xs text-[#a0a0ab] hover:text-white w-full py-2">
            Passer →
          </button>
        )}
      </div>
    );
  }

  // RESULT SCREEN
  if (phase === 'result' && answerResult) {
    const isCorrect = answerResult.is_correct;
    return (
      <div className="min-h-screen bg-[#0f0e17] flex flex-col items-center justify-center px-4 text-center">
        <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-4 ${isCorrect ? 'bg-[#06d6a0]/20' : 'bg-[#ff6b6b]/20'}`}>
          {isCorrect ? <CheckCircle size={40} className="text-[#06d6a0]" /> : <XCircle size={40} className="text-[#ff6b6b]" />}
        </div>
        <h2 className="text-xl font-bold text-white mb-1">{isCorrect ? 'Bonne réponse !' : 'Incorrect'}</h2>
        {isCorrect && <p className="text-[#06d6a0] font-bold text-lg mb-1">+{answerResult.points?.toLocaleString()} pts</p>}
        {answerResult.streak >= 3 && <p className="text-[#ffd93d] text-sm mb-1">{answerResult.streak}x 🔥 Flamme !</p>}
        <p className="text-white font-bold text-2xl mb-2">{score.toLocaleString()} pts total</p>
        {answerResult.explanation && (
          <div className="bg-white/5 border border-white/10 rounded-xl p-3 max-w-xs text-sm text-[#e8e8e8] mt-2">
            {answerResult.explanation}
          </div>
        )}
        <p className="text-[#a0a0ab] text-xs mt-4">Prochaine question...</p>
      </div>
    );
  }

  // LEADERBOARD
  if (phase === 'leaderboard') {
    const myRank = leaderboard.findIndex(r => r.nickname === nickname) + 1;
    return (
      <div className="min-h-screen bg-[#0f0e17] flex flex-col items-center justify-center px-4">
        <div className="flex items-center gap-2 mb-4">
          <Trophy size={20} className="text-[#ffd93d]" />
          <h2 className="text-lg font-bold text-white">Classement</h2>
        </div>
        <div className="w-full max-w-sm space-y-2 mb-4">
          {leaderboard.slice(0, 5).map((r, i) => (
            <div key={r.participant_id} className={`flex items-center gap-3 px-4 py-3 rounded-xl ${r.nickname === nickname ? 'bg-[#ff6b6b]/20 border border-[#ff6b6b]/30' : 'bg-white/5 border border-white/10'}`}>
              <span className="text-xl font-bold text-[#ffd93d] w-6">{['🥇', '🥈', '🥉'][i] || `${i + 1}.`}</span>
              <span className="flex-1 text-white text-sm font-medium">{r.nickname}</span>
              <span className="text-[#ff6b6b] font-bold text-sm">{r.score.toLocaleString()}</span>
            </div>
          ))}
        </div>
        {myRank > 5 && (
          <div className="bg-white/5 border border-[#ff6b6b]/30 rounded-xl px-4 py-2 text-sm">
            <span className="text-[#a0a0ab]">Votre position : </span>
            <span className="text-white font-bold">#{myRank} — {score.toLocaleString()} pts</span>
          </div>
        )}
        <p className="text-[#a0a0ab] text-xs mt-4">Question suivante dans quelques secondes...</p>
      </div>
    );
  }

  // ENDED
  if (phase === 'ended') {
    const myPos = finalRankings.findIndex(r => r.nickname === nickname) + 1;
    const myFinal = finalRankings.find(r => r.nickname === nickname);
    const winner = finalRankings[0];
    return (
      <div className="min-h-screen bg-[#0f0e17] flex flex-col items-center justify-center px-4">
        <div className="max-w-sm w-full text-center">
          <div className="text-5xl mb-3">{myPos === 1 ? '🏆' : myPos <= 3 ? '🥈' : '🎮'}</div>
          <h1 className="text-xl font-bold text-white mb-1">{quizTitle}</h1>
          <p className="text-[#a0a0ab] text-sm mb-4">Session terminée !</p>
          {winner && (
            <div className="bg-[#ffd93d]/10 border border-[#ffd93d]/30 rounded-xl p-3 mb-4">
              <p className="text-[#ffd93d] font-bold">Vainqueur : {winner.nickname}</p>
              <p className="text-white text-sm">{winner.score?.toLocaleString()} pts</p>
            </div>
          )}
          {myFinal && (
            <div className="bg-white/5 border border-white/10 rounded-xl p-3 mb-4">
              <p className="text-white font-bold">Votre score : {myFinal.score?.toLocaleString()} pts</p>
              <p className="text-[#a0a0ab] text-sm">Position #{myPos} sur {finalRankings.length}</p>
            </div>
          )}
          <button onClick={() => navigate('/quiz')} className="btn-primary w-full py-3 text-sm">Retour aux quizzes</button>
        </div>
      </div>
    );
  }

  return null;
}
